import { PDFDocument, PDFName, StandardFonts } from "pdf-lib";
import { useEffect, useState } from "react";
import PdfViewDrawer from "../../../../../components/PdfViewDrawer";
import { STATE_RECORD } from "../../../../../constants";
import useDisclosure from "../../../../../hooks/use-disclosure";
import { formatDate } from "../../../../../utils/date-time-utility";
import { getFormattedId, getValueFromObj } from "../../../../../utils/utils";
import { placesOfService } from "../add-super-bill-components/places-of-service";

// Extract MM / DD / YYYY using dayjs so timezone shifts from the ISO
// "T00:00:00Z" style backend dates do not move the day off by one.
const useGetMMDDYY = (d?: string) => {
  const formatter = { MM: "", DD: "", YY: "" };

  if (!d) return formatter;

  const fmtDate = formatDate(d);
  const splitter = fmtDate?.split("/");

  // if (!d.isValid()) return { MM: "", DD: "", YY: "" };
  return { MM: splitter?.[0], DD: splitter?.[1], YY: splitter?.[2] };
};

const POS_CODE_BY_ENUM: Record<string, string> = placesOfService.reduce(
  (acc, opt) => {
    acc[opt.value] = opt.label?.split(" - ")?.[0]?.trim() ?? "";
    return acc;
  },
  {} as Record<string, string>
);

const getGenderInitial = (gender?: string) => {
  if (!gender) return "";
  const g = gender.toUpperCase();
  if (g === "MALE" || g === "M") return "M";
  if (g === "FEMALE" || g === "F") return "F";
  return "";
};

const getInsSexState = (gender?: string): string =>
  ({ M: "MALE", F: "FEMALE" })[getGenderInitial(gender) as "M" | "F"] ?? "";

const getYesNoState = (v?: boolean | null) =>
  v === null ? "" : v ? "YES" : "NO";

// Box 27 acceptAssignment arrives as a string ("YES"/"NO") or boolean from the
// backend; normalise to the checkbox on-state.
const getAcceptAssignmentState = (v?: string | boolean | null) => {
  if (v === null || v === "") return "";
  const s = String(v).toUpperCase();
  if (s === "YES" || s === "TRUE") return "YES";
  if (s === "NO" || s === "FALSE") return "NO";
  return "";
};

// Box 1 insurance type checkbox. The CMS form only has Medicare / Medicaid /
// Tricare / Champva / Group / Feca / Other on-states, so the insurance planType
// is bucketed into the closest one. Unknown/empty leaves every box unticked.
const getInsuranceBox1State = (planType?: string): string => {
  switch (planType?.toUpperCase()) {
    case "MEDICARE":
      return "Medicare";
    case "MEDICAID":
      return "Medicaid";
    case "WORKERS_COMPENSATION":
    case "MOTOR_VEHICLE":
    case "UNINSURED":
    case "OTHER":
      return "Other";
    case undefined:
    case "":
      return "";
    default:
      // PPO / HMO / EPO / POS / PRIVATE / DENTAL / VISION / HSA / BEHAVIORAL_HEALTH
      return "Group";
  }
};

const formatPatientName = (fullName?: string) => {
  const tokens = fullName?.trim().split(/\s+/).filter(Boolean) ?? [];
  if (tokens.length <= 1) return tokens[0] ?? "";
  return `${tokens.at(-1)}, ${tokens.slice(0, -1).join(" ")}`;
};

const setExclusiveCheckboxState = (field: any, onState: string) => {
  const off = PDFName.of("Off");
  const on = onState ? PDFName.of(onState) : off;
  field.acroField.dict.set(PDFName.of("V"), on);
  field.acroField
    .getWidgets()
    .forEach((w: any) =>
      w.setAppearanceState(onState && w.getOnValue() === on ? on : off)
    );
};

const toPointerLetter = (pointer?: string) => {
  const p = pointer ? String(pointer).trim() : "";
  if (!p) return "";
  if (/^[A-La-l]$/.test(p)) return p.toUpperCase();
  const n = parseInt(p, 10);
  return n >= 1 && n <= 12 ? "ABCDEFGHIJKL"[n - 1] : "";
};

// Box 6 is a single checkbox with S / M / C / O widget on-states, so the
// relationship enum is bucketed into the matching on-state.
const getRelationshipBox6State = (relationship?: string): string => {
  if (!relationship) return "S";
  const rel = relationship.toUpperCase().replace(/\s+/g, "_");
  if (rel === "SELF") return "S";
  if (rel === "SPOUSE" || rel === "LIFE_PARTNER") return "M";
  if (
    rel === "CHILD" ||
    rel === "NATURAL_CHILD" ||
    rel === "FOSTER_CHILD" ||
    rel === "STEPCHILD"
  )
    return "C";
  return "O";
};

const getDiagnosisCodesFromObj = (
  diagnosisCodes?: Record<string, string>
): { code: string }[] => {
  if (!diagnosisCodes) return [];
  return Object.values(diagnosisCodes).map((val) => ({
    code: val?.split(" - ")?.[0]?.trim() ?? "",
  }));
};

const CMSForm = ({ disclosureHost }: { disclosureHost: any }) => {
  const prefillData = disclosureHost?.data;

  // patientInsurance arrives as an array from the backend — primary is the first entry,
  // secondary (if any) is the second. Older mocks used a plain object, so support both.
  const insuranceList: any[] = Array.isArray(prefillData?.patientInsurance)
    ? prefillData?.patientInsurance
    : prefillData?.patientInsurance
      ? [prefillData?.patientInsurance]
      : [];
  const primaryInsurance = insuranceList?.[0];
  const secondaryInsurance = insuranceList?.[1];

  const { MM, DD, YY } = useGetMMDDYY(prefillData?.patientDOB);

  const {
    MM: ill_MM,
    DD: ill_DD,
    YY: ill_YY,
  } = useGetMMDDYY(prefillData?.dateOfCurrentIllness);

  const {
    MM: other_MM,
    DD: other_DD,
    YY: other_YY,
  } = useGetMMDDYY(prefillData?.otherDate);

  const {
    MM: work_from_MM,
    DD: work_from_DD,
    YY: work_from_YY,
  } = useGetMMDDYY(prefillData?.unableToWorkFrom);

  const {
    MM: work_end_MM,
    DD: work_end_DD,
    YY: work_end_YY,
  } = useGetMMDDYY(prefillData?.unableToWorkTo);

  const {
    MM: hosp_from_MM,
    DD: hosp_from_DD,
    YY: hosp_from_YY,
  } = useGetMMDDYY(prefillData?.hospitalizationFrom);

  const {
    MM: hosp_end_MM,
    DD: hosp_end_DD,
    YY: hosp_end_YY,
  } = useGetMMDDYY(prefillData?.hospitalizationTo);

  const {
    MM: ins_MM,
    DD: ins_DD,
    YY: ins_YY,
  } = useGetMMDDYY(primaryInsurance?.dob ?? primaryInsurance?.insuredBirthDate);

  // Diagnosis codes: use diagnisisCodeData array if available, else extract from diagnosisCodes object
  const diagnosisCodeList =
    prefillData?.diagnisisCodeData?.length > 0
      ? prefillData?.diagnisisCodeData
      : getDiagnosisCodesFromObj(prefillData?.diagnosisCodes);

  // Insured address: fallback to subscriberAddress when primary insurance address is missing
  const insuredAddress =
    primaryInsurance?.address &&
    Object.values(primaryInsurance.address).some((v) => v)
      ? primaryInsurance.address
      : (primaryInsurance?.subscriberAddress ?? prefillData?.subscriberAddress);

  // Insured DOB: fallback to subscriberBirthDate
  const {
    MM: sub_MM,
    DD: sub_DD,
    YY: sub_YY,
  } = useGetMMDDYY(
    primaryInsurance?.subscriberBirthDate ?? prefillData?.subscriberBirthDate
  );

  const procCodes = prefillData?.patientProcedureCodes;

  const getDateOfService = (index: number) => {
    if (!procCodes?.[index]) return;

    return prefillData?.dateOfService;
  };

  const getPlaceOfService = (index: number) => {
    if (!procCodes?.[index]) return;

    const posEnum = prefillData?.placeOfService ?? prefillData?.posCode;
    return POS_CODE_BY_ENUM[posEnum] ?? posEnum ?? "";
  };

  const getRenderingProviderNPI = (index: number) => {
    if (!procCodes?.[index]) return;

    const npi = prefillData?.renderingProviderNPI;
    return npi !== null ? String(npi) : "";
  };

  const {
    MM: sv1_mm,
    DD: sv1_dd,
    YY: sv1_yy,
  } = useGetMMDDYY(getDateOfService(0));

  const {
    MM: sv2_mm,
    DD: sv2_dd,
    YY: sv2_yy,
  } = useGetMMDDYY(getDateOfService(1));

  const {
    MM: sv3_mm,
    DD: sv3_dd,
    YY: sv3_yy,
  } = useGetMMDDYY(getDateOfService(2));

  const {
    MM: sv4_mm,
    DD: sv4_dd,
    YY: sv4_yy,
  } = useGetMMDDYY(getDateOfService(3));

  const {
    MM: sv5_mm,
    DD: sv5_dd,
    YY: sv5_yy,
  } = useGetMMDDYY(getDateOfService(4));

  const {
    MM: sv6_mm,
    DD: sv6_dd,
    YY: sv6_yy,
  } = useGetMMDDYY(getDateOfService(5));

  const tarnsFilterStringOnlyProcedureCodes = prefillData?.patientProcedureCodes
    ? prefillData?.patientProcedureCodes?.reduce((acc: any, item: any) => {
        const code = item?.procedureCode
          ? Object.values(item?.procedureCode)?.reduce((a: any, c: any) => {
              a.push(c?.split("-")?.[0]);
              return a;
            }, [])
          : "";
        acc.push(code);
        return acc;
      }, [])
    : [];

  const tarnsFilterStringOnlyModifiers = prefillData?.patientProcedureCodes
    ? prefillData?.patientProcedureCodes?.reduce((acc: any, item: any) => {
        const curCodeModifiers = item?.modifiers
          ? Object.values(item?.modifiers)?.reduce((a: any, c: any) => {
              a.push(c?.split("-")?.[0]);
              return a;
            }, [])
          : "";
        acc.push(curCodeModifiers);
        return acc;
      }, [])
    : [];

  const getModifier = (index: number, subIndex: number) => {
    return tarnsFilterStringOnlyModifiers?.[index]?.[subIndex];
  };

  const getProcedureCode = (index: number, subIndex: number) => {
    return tarnsFilterStringOnlyProcedureCodes?.[index]?.[subIndex];
  };

  const getDiagnosis = (index: number) => {
    const proc = prefillData?.patientProcedureCodes?.[index];
    if (!proc) return "";
    return [
      proc.diagnosisFirstPointers,
      proc.diagnosisSecondPointers,
      proc.diagnosisThirdPointers,
      proc.diagnosisFourthPointers,
    ]
      .map(toPointerLetter)
      .filter(Boolean)
      .join("");
  };

  const getCharge = (index: number) => {
    return typeof prefillData?.patientProcedureCodes?.[index]?.charges ===
      "number"
      ? prefillData?.patientProcedureCodes?.[index]?.charges?.toFixed(2)
      : prefillData?.patientProcedureCodes?.[index]?.charges;
  };

  const getQuantity = (index: number) => {
    return prefillData?.patientProcedureCodes?.[index]?.quantity;
  };

  const ptPhone = prefillData?.patientPhone;

  // ECRH-5370: Box 32 (Service Facility Location) billing address.
  // The Claim and CMS-1500 print responses expose the resolved address as
  // `serviceLocationBillingAddress` when a Service Location is selected, and
  // `providerGroupBillingAddress` (the Provider Group Profile billing address)
  // when none is selected — e.g. for Telehealth encounters. Fall back to the
  // legacy `serviceLocationAddress` for older payloads.
  const box32BillingAddress =
    prefillData?.serviceLocationBillingAddress ??
    prefillData?.providerGroupBillingAddress ??
    prefillData?.serviceLocationAddress;

  // Box 33 — Billing Provider (Practice Group) address & contact. Prefer the
  // practice billing details resolved onto the claim; fall back to the legacy
  // billing provider fields.
  const box33BillingAddress =
    prefillData?.practiceBillingAddress ?? prefillData?.billingProviderAddress;
  const box33Phone =
    prefillData?.practiceContact ?? prefillData?.billingProviderPhone;

  const cms1500DummyPrefill = {
    "NUCC USE": prefillData?.nuccUse,
    "40": "",
    "57": "",
    "58": "",
    "41": "",
    "50": "",
    "73": "",
    "74": "",
    "85": "",
    "96": "",
    "99icd": "0",
    "135": "",
    "157": "",
    "179": "",
    "201": "",
    "223": "",
    "245": "",
    "276": "",
    "Clear Form": "",
    insurance_name:
      primaryInsurance?.insurancePayer?.payerName ?? primaryInsurance?.planName,
    // Carrier header shows the payer name only; the address lines below it are
    // intentionally left blank.
    // insurance_address: insuredAddress?.line1,
    // insurance_address2: insuredAddress?.line2,
    // insurance_city_state_zip: insuredAddress?.city
    //   ? insuredAddress?.city +
    //     ", " +
    //     (STATE_RECORD[insuredAddress?.state as string] ?? "") +
    //     ", " +
    //     (insuredAddress?.zipcode ?? "")
    //   : "",
    pt_name: formatPatientName(prefillData?.patientName),
    // pt_name: "ELLIS, ROBERT",
    insurance_id: primaryInsurance?.insuranceId ?? prefillData?.insuranceId,
    ins_name:
      primaryInsurance?.insuredLastName || primaryInsurance?.insuredFirstName
        ? (primaryInsurance?.insuredLastName ?? "") +
          (primaryInsurance?.insuredLastName &&
          primaryInsurance?.insuredFirstName
            ? ", "
            : "") +
          (primaryInsurance?.insuredFirstName ?? "")
        : (primaryInsurance?.subscriberLastName ??
              prefillData?.subscriberLastName) ||
            (primaryInsurance?.subscriberFirstName ??
              prefillData?.subscriberFirstName)
          ? (primaryInsurance?.subscriberLastName ??
              prefillData?.subscriberLastName ??
              "") +
            ((primaryInsurance?.subscriberLastName ??
              prefillData?.subscriberLastName) &&
            (primaryInsurance?.subscriberFirstName ??
              prefillData?.subscriberFirstName)
              ? ", "
              : "") +
            (primaryInsurance?.subscriberFirstName ??
              prefillData?.subscriberFirstName ??
              "")
          : "",
    insurance_type: getInsuranceBox1State(primaryInsurance?.planType),
    birth_mm: MM,
    birth_dd: DD,
    birth_yy: YY,
    sex: getGenderInitial(prefillData?.patientGender),

    // ? need to check mapping for street
    pt_street: prefillData?.patientAddress?.line1,

    pt_city: prefillData?.patientAddress?.city,
    pt_state: STATE_RECORD[prefillData?.patientAddress?.state as string],
    pt_zip: prefillData?.patientAddress?.zipcode
      ? prefillData?.patientAddress?.zipcode?.slice(0, 5) +
        "-" +
        prefillData?.patientAddress?.zipcode?.slice(5)
      : "",
    pt_AreaCode: ptPhone?.slice(1, 4),
    pt_phone: ptPhone?.slice(5),
    rel_to_ins: getRelationshipBox6State(
      primaryInsurance?.insuredRelationshipWithPatient ??
        prefillData?.insuredRelationshipWithPatient
    ),
    ins_street: insuredAddress?.line1,
    ins_city: insuredAddress?.city,
    ins_state: STATE_RECORD[insuredAddress?.state as string],
    ins_zip: insuredAddress?.zipcode,
    "ins_phone area": (
      primaryInsurance?.phoneNumber ??
      (
        primaryInsurance?.subscriberMobileNumber ??
        prefillData?.subscriberMobileNumber
      )?.replace(/\D/g, "")
    )?.slice(0, 3),
    ins_phone: (
      primaryInsurance?.phoneNumber ??
      (
        primaryInsurance?.subscriberMobileNumber ??
        prefillData?.subscriberMobileNumber
      )?.replace(/\D/g, "")
    )?.slice(3),
    ins_policy:
      primaryInsurance?.groupId ?? primaryInsurance?.policyNumber ?? "",
    ins_dob_mm: ins_MM || sub_MM,
    ins_dob_dd: ins_DD || sub_DD,
    ins_dob_yy: ins_YY || sub_YY,
    ins_sex: getInsSexState(
      primaryInsurance?.insuredGender ??
        primaryInsurance?.subscriberGender ??
        prefillData?.subscriberGender
    ),
    other_ins_name:
      secondaryInsurance?.insuredLastName ||
      secondaryInsurance?.insuredFirstName
        ? (secondaryInsurance?.insuredLastName ?? "") +
          (secondaryInsurance?.insuredLastName &&
          secondaryInsurance?.insuredFirstName
            ? ", "
            : "") +
          (secondaryInsurance?.insuredFirstName ?? "")
        : "",
    other_ins_policy:
      secondaryInsurance?.groupId ?? secondaryInsurance?.policyNumber ?? "",
    other_ins_plan_name: secondaryInsurance?.planName ?? "",
    ins_signature: "",
    ins_benefit_plan: "",
    ins_plan_name: primaryInsurance?.planName,
    pt_signature: "",
    pt_date: "",
    cur_ill_mm: ill_MM,
    cur_ill_dd: ill_DD,
    cur_ill_yy: ill_YY,
    ref_physician: prefillData?.referringProvider
      ? getValueFromObj(prefillData?.referringProvider)
      : "",
    id_physician: prefillData?.referringProviderNPI
      ? String(prefillData?.referringProviderNPI)
      : "",
    "physician number 17a1": "",
    "physician number 17a": "",
    sim_ill_mm: other_MM,
    sim_ill_dd: other_DD,
    sim_ill_yy: other_YY,
    work_mm_from: work_from_MM,
    work_dd_from: work_from_DD,
    work_yy_from: work_from_YY,
    work_mm_end: work_end_MM,
    work_dd_end: work_end_DD,
    work_yy_end: work_end_YY,
    hosp_mm_from: hosp_from_MM,
    hosp_dd_from: hosp_from_DD,
    hosp_yy_from: hosp_from_YY,
    hosp_mm_end: hosp_end_MM,
    hosp_dd_end: hosp_end_DD,
    hosp_yy_end: hosp_end_YY,
    // Box 20 — Outside Lab? Only mark/charge when an outside lab charge exists;
    // the claim total belongs in Box 28 (t_charge), not here.
    lab: prefillData?.outsideLabCharge ? "YES" : "",
    charge: prefillData?.outsideLabCharge ?? "",
    medicaid_resub: prefillData?.resubmissionCode ?? "",
    original_ref: prefillData?.originalReferenceNumber ?? "",
    prior_auth: prefillData?.priorAuthorization,
    emg1: "",
    local1a: "",
    Suppla: "",
    Supplb: "",
    Supplc: "",
    Suppld: "",
    Supple: "",
    emg2: "",
    emg3: "",
    emg4: "",
    emg5: "",
    emg6: "",
    tax_id: prefillData?.taxId ?? "",
    pt_account: prefillData?.uuid ? getFormattedId(prefillData?.uuid) : "",
    assignment: getAcceptAssignmentState(prefillData?.acceptAssignment),
    t_charge: prefillData?.total ? `${prefillData?.total.toFixed(2)}` : "",
    amt_paid: `$${(Number(prefillData?.paidAmount) || 0).toFixed(2)}`,
    physician_signature: prefillData?.renderingProviderName ?? "",
    physician_date: prefillData?.providerSignatureDate
      ? formatDate(prefillData?.providerSignatureDate)
      : "",
    // Box 32 — Service Facility Location: name + street on their own lines and
    // city/state/zip on the third line, sourced from the resolved Service
    // Location / Provider Group billing address (ECRH-5370).
    fac_name: getValueFromObj(prefillData?.serviceLocation) ?? "",
    fac_street: box32BillingAddress?.line1 ?? "",
    fac_location: box32BillingAddress
      ? (box32BillingAddress?.city ?? "") +
        (box32BillingAddress?.city ? ", " : "") +
        (STATE_RECORD[box32BillingAddress?.state as string] ?? "") +
        " " +
        (box32BillingAddress?.zipcode ?? "")
      : "",
    // Box 33 — Billing Provider = Practice Group (Billing Entity). Prefer the
    // practice details resolved onto the claim; fall back to legacy billing
    // provider fields.
    doc_name:
      prefillData?.practiceName ??
      prefillData?.billingProviderName ??
      getValueFromObj(prefillData?.billingProvider) ??
      "",
    doc_street: box33BillingAddress?.line1 ?? "",
    doc_location: box33BillingAddress
      ? (box33BillingAddress?.city ?? "") +
        (box33BillingAddress?.city ? ", " : "") +
        (STATE_RECORD[box33BillingAddress?.state as string] ?? "") +
        " " +
        (box33BillingAddress?.zipcode ?? "")
      : "",
    "doc_phone area": box33Phone?.slice(0, 3) ?? "",
    doc_phone: box33Phone?.slice(3) ?? "",
    pin:
      prefillData?.practiceNpi ??
      (prefillData?.billingProviderNPI
        ? String(prefillData?.billingProviderNPI)
        : (prefillData?.billingProviderNpi ?? "")),
    grp: prefillData?.providerGroupNpi ?? "",
    employment: getYesNoState(prefillData?.conditionRelatedToEmployment),
    pt_auto_accident: getYesNoState(
      prefillData?.conditionRelatedToAutoAccident
    ),
    accident_place: prefillData?.conditionRelatedToAutoAccident
      ? (STATE_RECORD[prefillData?.state as string] ?? "")
      : "",
    other_accident: getYesNoState(prefillData?.conditionRelatedToOtherAccident),
    diagnosis1: diagnosisCodeList?.[0]?.code,
    diagnosis2: diagnosisCodeList?.[1]?.code,
    diagnosis3: diagnosisCodeList?.[2]?.code,
    diagnosis4: diagnosisCodeList?.[3]?.code,
    diagnosis5: diagnosisCodeList?.[4]?.code,
    diagnosis6: diagnosisCodeList?.[5]?.code,
    diagnosis7: diagnosisCodeList?.[6]?.code,
    diagnosis8: diagnosisCodeList?.[7]?.code,
    diagnosis9: diagnosisCodeList?.[8]?.code,
    diagnosis10: diagnosisCodeList?.[9]?.code,
    diagnosis11: diagnosisCodeList?.[10]?.code,
    diagnosis12: diagnosisCodeList?.[11]?.code,
    ssn: prefillData?.taxId ? "EIN" : "",

    //procedure codes data table format

    //  Modifiers {row 1}
    mod1: getModifier(0, 0),
    mod1a: getModifier(0, 1),
    mod1b: getModifier(0, 2),
    mod1c: getModifier(0, 3),
    diag1: getDiagnosis(0),
    cpt1: getProcedureCode(0, 0),
    // rendering provider data
    local1: getRenderingProviderNPI(0),
    place1: getPlaceOfService(0),

    sv1_mm_from: sv1_mm,
    sv1_dd_from: sv1_dd,
    sv1_yy_from: sv1_yy?.slice(2),
    sv1_mm_end: sv1_mm,
    sv1_dd_end: sv1_dd,
    sv1_yy_end: sv1_yy?.slice(2),

    ch1: getCharge(0),
    day1: getQuantity(0),

    //  Modifiers {row 2}
    mod2: getModifier(1, 0),
    mod2a: getModifier(1, 1),
    mod2b: getModifier(1, 2),
    mod2c: getModifier(1, 3),
    diag2: getDiagnosis(1),
    cpt2: getProcedureCode(1, 0),
    local2: getRenderingProviderNPI(1),
    local2a: "",
    place2: getPlaceOfService(1),

    sv2_mm_from: sv2_mm,
    sv2_dd_from: sv2_dd,
    sv2_yy_from: sv2_yy?.slice(2),
    sv2_mm_end: sv2_mm,
    sv2_dd_end: sv2_dd,
    sv2_yy_end: sv2_yy?.slice(2),

    ch2: getCharge(1),
    day2: getQuantity(1),

    //  Modifiers {row 3}
    mod3: getModifier(2, 0),
    mod3a: getModifier(2, 1),
    mod3b: getModifier(2, 2),
    mod3c: getModifier(2, 3),
    diag3: getDiagnosis(2),
    cpt3: getProcedureCode(2, 0),
    local3: getRenderingProviderNPI(2),
    local3a: "",
    place3: getPlaceOfService(2),

    sv3_mm_from: sv3_mm,
    sv3_dd_from: sv3_dd,
    sv3_yy_from: sv3_yy?.slice(2),
    sv3_mm_end: sv3_mm,
    sv3_dd_end: sv3_dd,
    sv3_yy_end: sv3_yy?.slice(2),
    ch3: getCharge(2),
    day3: getQuantity(2),

    //  Modifiers {row 4}
    mod4: getModifier(3, 0),
    mod4a: getModifier(3, 1),
    mod4b: getModifier(3, 2),
    mod4c: getModifier(3, 3),
    diag4: getDiagnosis(3),
    cpt4: getProcedureCode(3, 0),
    local4: getRenderingProviderNPI(3),
    local4a: "",
    place4: getPlaceOfService(3),

    sv4_mm_from: sv4_mm,
    sv4_dd_from: sv4_dd,
    sv4_yy_from: sv4_yy?.slice(2),
    sv4_mm_end: sv4_mm,
    sv4_dd_end: sv4_dd,
    sv4_yy_end: sv4_yy?.slice(2),

    ch4: getCharge(3),
    day4: getQuantity(3),

    //  Modifiers {row 5}
    mod5: getModifier(4, 0),
    mod5a: getModifier(4, 1),
    mod5b: getModifier(4, 2),
    mod5c: getModifier(4, 3),
    //  Diagnosis {row 5}
    diag5: getDiagnosis(4),
    cpt5: getProcedureCode(4, 0),
    local5a: "",
    local5: getRenderingProviderNPI(4),
    place5: getPlaceOfService(4),

    sv5_mm_from: sv5_mm,
    sv5_dd_from: sv5_dd,
    sv5_yy_from: sv5_yy?.slice(2),
    sv5_mm_end: sv5_mm,
    sv5_dd_end: sv5_dd,
    sv5_yy_end: sv5_yy?.slice(2),

    ch5: getCharge(4),
    day5: getQuantity(4),

    //  Modifiers {row 6}
    mod6: getModifier(5, 0),
    mod6a: getModifier(5, 1),
    mod6b: getModifier(5, 2),
    mod6c: getModifier(5, 3),
    //  Diagnosis {row 6}
    diag6: getDiagnosis(5),
    cpt6: getProcedureCode(5, 0),
    local6a: "",
    local6: getRenderingProviderNPI(5),
    place6: getPlaceOfService(5),

    sv6_mm_from: sv6_mm,
    sv6_dd_from: sv6_dd,
    sv6_yy_from: sv6_yy?.slice(2),
    sv6_mm_end: sv6_mm,
    sv6_dd_end: sv6_dd,
    sv6_yy_end: sv6_yy?.slice(2),

    ch6: getCharge(5),
    day6: getQuantity(5),

    type6: "",
    type5: "",
    type1: "",
    type2: "",
    type3: "",
    type4: "",
    // Box 32a — Service Facility NPI: backend maps this to providerGroupNpi.
    pin1: prefillData?.providerGroupNpi
      ? String(prefillData?.providerGroupNpi)
      : "",
    grp1: "",
    Suppl: "",
    plan1: "",
    plan2: "",
    plan3: "",
    plan4: "",
    plan5: "",
    plan6: "",
    epsdt1: "",
    epsdt3: "",
    epsdt6: "",
    epsdt5: "",
    epsdt4: "",
    epsdt2: "",
  };

  const [_loading, setLoading] = useState(false);
  const previewCmsForm = useDisclosure(false, {
    onClose: disclosureHost.close,
  });

  const fillPdf = async () => {
    setLoading(true);
    // Fetch the PDF template
    const existingPdfBytes = await fetch("/cms-form/form-cms-1500.pdf").then(
      (res) => res.arrayBuffer()
    );
    const pdfDoc = await PDFDocument.load(existingPdfBytes);

    // Load the Times New Roman Bold font
    const customFont = await pdfDoc.embedStandardFont(
      StandardFonts.CourierBold
    );

    // Get the form
    const form = pdfDoc.getForm();
    const exclusiveStateFields = new Set([
      "sex",
      "ins_sex",
      "employment",
      "pt_auto_accident",
      "other_accident",
      "ssn",
      "insurance_type",
      "lab",
      "assignment",
      "rel_to_ins",
    ]);
    Object.entries(cms1500DummyPrefill).forEach(([pdfKey, value]) => {
      if (!pdfKey) return;
      try {
        const field: any = form.getField(pdfKey);
        if (exclusiveStateFields.has(pdfKey)) {
          setExclusiveCheckboxState(field, String(value || ""));
        } else if (field.check?.bind) {
          // Checkbox field
          value ? field.check() : field.uncheck?.();
        } else if (field.setText?.bind) {
          // Text field
          field.setText(String(value || "").toUpperCase());
          field.updateAppearances(customFont);
        } else if (field.select?.bind) {
          // Dropdown/select field
          field.select(String(value));
          field.updateAppearances(customFont);
        }
      } catch (e) {
        // Field not found, ignore
      }
    });

    const pdfBytes: any = await pdfDoc.save();

    const blob = new Blob([pdfBytes], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    previewCmsForm.open(url);
    // const link = document.createElement("a");
    // link.href = url;
    // link.download = "cms-1500-prefilled.pdf";
    // document.body.appendChild(link);
    // link.click();
    // document.body.removeChild(link);
    // URL.revokeObjectURL(url);
    setLoading(false);
  };

  useEffect(() => {
    if (!prefillData) return;
    fillPdf();
  }, [prefillData]);

  return (
    <PdfViewDrawer
      disclosureHost={previewCmsForm}
      file={previewCmsForm?.data}
      w="90vw"
    />
  );
};

export default CMSForm;
